import { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AppHeader, AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const AdminModuleAccess = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard');
      return;
    }
    loadUsers();
    loadModules();
  }, [user]);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data || []);
      setError('');
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Kon gebruikers niet laden');
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    try {
      const response = await axios.get(`${API}/admin/modules`);
      setModules(response.data || []);
    } catch (err) {
      console.error('Failed to load modules:', err);
      setModules([]);
    }
  };

  const openDialogForUser = async (targetUser) => {
    try {
      const response = await axios.get(`${API}/admin/users/${targetUser.id}/modules`);
      setSelectedUser(targetUser);
      setSelectedModules(response.data?.modules || []);
      setDialogOpen(true);
      setError('');
    } catch (err) {
      console.error('Failed to load user modules:', err);
      setError('Kon moduletoegang niet ophalen');
    }
  };

  const toggleModule = (moduleId, checked) => {
    setSelectedModules((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, moduleId]));
      }
      return prev.filter((id) => id !== moduleId);
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/modules`, {
        modules: selectedModules,
      });
      setDialogOpen(false);
      setError('');
    } catch (err) {
      console.error('Failed to save modules:', err);
      setError(err.response?.data?.detail || 'Kon moduletoegang niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return [];
    }
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return users;
    }
    return users.filter((item) => {
      const name = (item.full_name || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, searchTerm]);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader
        user={user}
        currentPage="admin-modules"
        onNavigate={handleNavigate}
        onLogout={logout}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-1">Moduletoegang beheren</h2>
          <p className="text-gray-600">
            Bepaal welke modules elke gebruiker mag zien binnen ProjectLeads.
          </p>
        </div>

        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Gebruikers</CardTitle>
            <CardDescription>Selecteer een gebruiker om modules te beheren</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Zoek op naam of e-mailadres..."
              />
            </div>
            {loading ? (
              <div className="text-center py-12 text-gray-500">Gegevens laden...</div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Geen gebruikers gevonden. Pas je zoekopdracht aan.
                  </div>
                ) : (
                  filteredUsers.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start justify-between p-4 border border-gray-200 rounded-lg"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.full_name}</h3>
                      <p className="text-sm text-gray-600">{item.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant={item.role === 'admin' ? 'default' : item.role === 'super_admin' ? 'destructive' : 'secondary'}>
                          {item.role}
                        </Badge>
                        <Badge variant={item.is_active ? 'default' : 'destructive'}>
                          {item.is_active ? 'Actief' : 'Inactief'}
                        </Badge>
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => openDialogForUser(item)}>
                      Moduletoegang aanpassen
                    </Button>
                  </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Moduletoegang aanpassen</DialogTitle>
            <DialogDescription>
              Bepaal welke pagina&apos;s {selectedUser?.full_name} kan bekijken.
            </DialogDescription>
          </DialogHeader>

          <Separator className="my-4" />

          <div className="space-y-4">
            {modules.length === 0 ? (
              <p className="text-sm text-gray-600">Geen modules gevonden.</p>
            ) : (
              modules.map((module) => (
                <label
                  key={module.id}
                  className="flex items-center justify-between border border-gray-200 rounded-lg p-3 hover:border-emerald-300 transition-colors cursor-pointer"
                >
                  <div>
                    <p className="font-medium text-gray-900">{module.label}</p>
                    {module.description && (
                      <p className="text-sm text-gray-600">{module.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Module-id: {module.id}</p>
                  </div>
                  <Checkbox
                    checked={selectedModules.includes(module.id)}
                    onCheckedChange={(checked) => toggleModule(module.id, checked)}
                  />
                </label>
              ))
            )}
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminModuleAccess;
